// Componentes
import { Card, Avatar, Box, Typography, alpha, Tooltip } from '@mui/material';

// Hooks
import useIsUserSub from './Hooks/useIsUserSub';
import useTranslation from 'Hooks/useTranslation';
import useEvaluation from './Hooks/useEvaluation';
import useRequestOpportunity from './Hooks/useRequestOpportunity';

// Sub-componentes de oportunidades
import AttemptsCounter from './Components/AttemptsCounter';
import RequestOpportunityButton from './Components/RequestOpportunityButton';
import OpportunityStatusBanner from './Components/OpportunityStatusBanner';
import ConfirmRequestDialog from './Components/ConfirmRequestDialog';

// Otros
import styled from '@emotion/styled';
import { colors } from 'Constants/Colors';
import { UserEvaluationI, OpportunityRequestI } from 'types';
import { useCallback, useState } from 'react';
import { Div } from 'Components/Div';
import type { OpportunityRequestCreatedI } from 'Api/axios/evaluation-opportunity-requests-api';

// === Emotion Components ===

interface CustomCardProps {
  disableHover?: boolean;
}

const CustomCard = styled(Card)<CustomCardProps>`
  display: flex;
  flex-direction: column;
  width: 300px;
  min-height: 350px;
  padding: 20px;
  transition: background-color 0.3s ease;
  border-radius: 25px;
  border: 1px solid ${alpha(colors.BLACK, 0.07)};

  ${({ disableHover }) =>
    !disableHover &&
    `
  cursor: pointer;
  &:hover {
      background-color: ${alpha(colors.BLUE, 0.05)};
    }
  `}
`;

const CardContent = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 20px;
  border-bottom: 1px solid ${alpha(colors.BLACK, 0.1)};
`;

const CustomAvatarContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

interface EmployeeEvaluationStatusProps {
  isApproved: boolean;
}

const EmployeeEvaluationStatus = styled(Typography)<EmployeeEvaluationStatusProps>`
  font-size: 14px;
  font-weight: 400;
  color: ${({ isApproved }) => (isApproved ? colors.GREEN : colors.RED)};
  font-weight: 600;
  background-color: ${({ isApproved }) => (isApproved ? alpha(colors.GREEN, 0.1) : alpha(colors.RED, 0.1))};
  padding: 5px 10px;
  border-radius: 30px;
`;

const CustomAvatar = styled(Avatar)`
  width: 60px;
  height: 60px;
  background-color: ${colors.BLUE_LIGHT_TRANSPARENT};
`;

const CardHeader = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`;

const CardTitle = styled(Typography)`
  font-size: 16px;
  font-weight: 600;
`;

const CardDescription = styled(Typography)`
  font-size: 14px;
  font-weight: 400;
  color: ${alpha(colors.BLACK, 0.5)};
`;

const CardFooter = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 16px;
  padding-bottom: 4px;
  align-items: center;
`;

const EvaluationStatus = styled(Typography)`
  font-size: 14px;
  font-weight: 400;
  color: ${alpha(colors.BLACK, 0.5)};
`;

interface EvaluationScoreProps {
  isApproved: boolean;
}

const EvaluationScore = styled(Typography)<EvaluationScoreProps>`
  font-size: 14px;
  font-weight: 400;
  color: ${({ isApproved }) => (isApproved ? colors.GREEN : colors.RED)};
  background-color: ${({ isApproved }) => (isApproved ? alpha(colors.GREEN, 0.1) : alpha(colors.RED, 0.1))};
  padding: 5px 10px;
  border-radius: 5px;
`;

const MiniEvaluationScore = styled(EvaluationScore)`
  font-size: 14px;
`;

// TODO: Refactorizar código
interface Props {
  courseId: string;
  onClick?: () => void;
  isEmployee: boolean;
  courseName: string;
  userEvaluation: UserEvaluationI;
}

export default function EvaluationCard({
  userEvaluation,
  courseId,
  onClick = () => {},
  isEmployee,
  courseName,
}: Props) {
  const { t2 } = useTranslation('CourseDetails');
  const { isUserSub } = useIsUserSub(courseId);
  const {
    icon,
    typeLabel,
    isManualEvaluation,
    isAutoEvaluation,
    attemptsExhausted,
    canRequestOpportunity,
    startTest,
  } = useEvaluation(userEvaluation, courseName, courseId);

  // Local state for opportunityRequest (updated optimistically after POST/PATCH)
  const [localOpportunityRequest, setLocalOpportunityRequest] = useState<OpportunityRequestI | null | undefined>(
    userEvaluation.opportunityRequest
  );

  // Dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { submitting, inlineError, requestOpportunity } = useRequestOpportunity(
    userEvaluation.lessonId,
    (created: OpportunityRequestCreatedI) => {
      setLocalOpportunityRequest({ id: created.id, status: created.status, comment: null });
      setConfirmOpen(false);
    }
  );

  // Re-derive canRequestOpportunity using local state
  const effectiveOpportunityRequest = localOpportunityRequest;
  const isBlockingRequest =
    effectiveOpportunityRequest != null &&
    (effectiveOpportunityRequest.status === 'pending' ||
      effectiveOpportunityRequest.status === 'approved' ||
      effectiveOpportunityRequest.status === 'rejected');
  const effectiveCanRequest = canRequestOpportunity && !isBlockingRequest;

  const handleClick = () => {
    if (isManualEvaluation) return null;
    if (!isUserSub) return null;

    // CONFLICT 3 resolution: allow starting the test if opportunity is approved
    const isOpportunityApproved = effectiveOpportunityRequest?.status === 'approved';
    const isEvaluationFinish = !!userEvaluation.userEvaluationId && !isOpportunityApproved && !hasAttemptsRemaining;

    if (isEvaluationFinish) return null;

    startTest();
  };

  /**
   * Método para manejar el [click] de la tarjeta
   */
  const onClickCard = useCallback(() => {
    if (isManualEvaluation) {
      onClick?.();
    } else {
      handleClick();
    }
  }, [isManualEvaluation, onClick, handleClick]);

  // TODO: Refactorizar todo este mugrero (viene desde base de datos el problema de "isDraft")
  const hasGrade = userEvaluation.grade !== null;

  const isApproved = userEvaluation.grade >= 70;

  // @s1: when the employee still has automatic attempts remaining, don't show "failed"
  const hasAttemptsRemaining =
    typeof userEvaluation.attemptsCount === 'number' &&
    typeof userEvaluation.maxAttempts === 'number' &&
    userEvaluation.attemptsCount < userEvaluation.maxAttempts;

  const showEmployeeStatus =
    ((hasGrade && !isManualEvaluation) || (userEvaluation.hideScore === false && hasGrade)) &&
    !(!isApproved && hasAttemptsRemaining);

  const showAdminGrade = !isEmployee && isManualEvaluation && userEvaluation.hideScore === false;

  const showManualEvaluationStatus = !isEmployee && isManualEvaluation;

  const notUserEvaluated = userEvaluation.total === 0 && userEvaluation.totalPublished === 0;

  return (
    <CustomCard onClick={onClickCard} disableHover={isEmployee && isManualEvaluation} elevation={0}>
      <CardContent>
        <CustomAvatarContainer>
          <CustomAvatar>{icon}</CustomAvatar>
          {showEmployeeStatus ? (
            <EmployeeEvaluationStatus isApproved={isApproved}>
              {isApproved ? t2('approved') : t2('failed')}
            </EmployeeEvaluationStatus>
          ) : null}
        </CustomAvatarContainer>
        <CardHeader>
          <CardTitle>{userEvaluation.name}</CardTitle>
          <CardDescription>{typeLabel}</CardDescription>
        </CardHeader>
      </CardContent>
      <CardFooter>
        {showManualEvaluationStatus ? (
          notUserEvaluated ? (
            <Tooltip title={t2('not_saved_yet_tooltip')}>
              <EvaluationStatus>{t2('not_saved_yet')}</EvaluationStatus>
            </Tooltip>
          ) : (
            <Div display="flex" flexDirection="column" gap={1} alignItems="center" justifyContent="center">
              <EvaluationStatus>
                {t2('evaluated')} {`${userEvaluation.totalPublished}/${userEvaluation.total}`}
              </EvaluationStatus>
              {showAdminGrade && (
                <MiniEvaluationScore isApproved={isApproved}>
                  {t2('your_score_is')}
                  <Typography component="span" fontWeight={600} fontSize={14}>
                    {userEvaluation.grade ?? 0}
                  </Typography>
                </MiniEvaluationScore>
              )}
            </Div>
          )
        ) : userEvaluation.hideScore === false ? (
          <EvaluationScore isApproved={isApproved}>
            {t2('your_score_is')}
            <Typography component="span" fontWeight={600}>
              {userEvaluation.grade ?? 0}
            </Typography>
          </EvaluationScore>
        ) : (
          <EvaluationStatus>{t2('not_evaluated_yet')}</EvaluationStatus>
        )}

        {/* Opportunity features — automatic evaluations only */}
        {isAutoEvaluation && (
          <>
            <AttemptsCounter
              attemptsCount={userEvaluation.attemptsCount}
              maxAttempts={userEvaluation.maxAttempts}
              isAutoEvaluation={isAutoEvaluation}
            />

            <OpportunityStatusBanner opportunityRequest={effectiveOpportunityRequest} />
            {inlineError && (
              <Typography variant="body2" color="error.main" align="center">
                {inlineError}
              </Typography>
            )}
            <RequestOpportunityButton
              canRequestOpportunity={effectiveCanRequest}
              onRequest={() => setConfirmOpen(true)}
            />
            <ConfirmRequestDialog
              open={confirmOpen}
              onConfirm={requestOpportunity}
              onCancel={() => setConfirmOpen(false)}
            />
          </>
        )}
      </CardFooter>
    </CustomCard>
  );
}
